import { AlertCircle, CheckCircle2, FolderTree, Sparkles } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Controller, SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card } from "../../../components/card";
import { DatePickerV3 } from "../../../components/date-picker-v3";
import { Input } from "../../../components/input";
import { Label } from "../../../components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/select";
import { Textarea } from "../../../components/textarea";
import {
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_OPTIONS,
  CAMPAIGN_STATUSES,
} from "../types/campaign-status.constants";

function createCampaignDateSchema(requiredMessage: string) {
  return z
    .string()
    .min(1, requiredMessage)
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Please enter a valid date.");
}

export const campaignEditorFormSchema = z
  .object({
    name: z.string().trim().min(1, "Campaign name is required."),
    description: z.string().trim(),
    status: z.nativeEnum(CAMPAIGN_STATUSES),
    startDate: createCampaignDateSchema("Start date is required."),
    endDate: createCampaignDateSchema("End date is required."),
  })
  .refine(({ startDate, endDate }) => new Date(endDate) >= new Date(startDate), {
    message: "End date must be on or after start date.",
    path: ["endDate"],
  });

export type CampaignEditorFormValues = z.infer<typeof campaignEditorFormSchema>;
export type CampaignEditorFormHandle = {
  submit: () => void;
};

interface CampaignEditorFormProps {
  loading: boolean;
  saving?: boolean;
  error?: string | null;
  defaultValues: CampaignEditorFormValues;
  onSubmit: (values: CampaignEditorFormValues) => Promise<void> | void;
}

function toFormValues(values: CampaignEditorFormValues): CampaignEditorFormValues {
  return {
    name: values.name || "",
    description: values.description || "",
    status: values.status || CAMPAIGN_STATUSES.Unactive,
    startDate: values.startDate || "",
    endDate: values.endDate || "",
  };
}

function toPickerDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return undefined;

  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day);
}

function toDateValue(date: Date | undefined) {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const CampaignEditorForm = forwardRef<CampaignEditorFormHandle, CampaignEditorFormProps>(
  function CampaignEditorForm({ loading, saving = false, error, defaultValues, onSubmit }, ref) {
    const formRef = useRef<HTMLFormElement | null>(null);
    const {
      control,
      handleSubmit,
      reset,
      watch,
      formState: { errors },
    } = useForm<CampaignEditorFormValues>({
      defaultValues: toFormValues(defaultValues),
      mode: "onChange",
      resolver: zodResolver(campaignEditorFormSchema),
    });

    const watchedValues = watch();
    const nameValue = watchedValues.name ?? "";
    const descriptionValue = watchedValues.description ?? "";
    const statusValue = watchedValues.status ?? "";
    const startDateValue = watchedValues.startDate ?? "";
    const endDateValue = watchedValues.endDate ?? "";

    useEffect(() => {
      reset(toFormValues(defaultValues));
    }, [defaultValues, reset]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSuccess)();
      },
    }));

    const onSuccess: SubmitHandler<CampaignEditorFormValues> = (data) => onSubmit(data);

    return (
      <div className="min-h-full bg-[linear-gradient(180deg,#F7FBF9_0%,#FFFFFF_22%)]">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 py-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Card className="rounded-[20px] border-[#DDEBE3] p-6 shadow-[0px_18px_45px_rgba(0,66,50,0.08)]">
            {error && (
              <div className="mb-4 rounded-xl border border-red-400 bg-red-100 p-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              </div>
            )}

            <form
              ref={formRef}
              id="campaign-editor-form"
              className="space-y-8"
            >
              <div className="rounded-[18px] border border-[#DCEFE3] bg-[linear-gradient(135deg,#F3FFF7_0%,#FFFFFF_100%)] p-5">
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl bg-[#E8FFF0] p-3 text-[#00A86B]">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#004232]">Campaign Identity</h2>
                    <p className="mt-1 text-sm text-[#5D6B63]">
                      Start with the core information. You can attach programs and opportunities
                      next.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign-name">Campaign Name *</Label>
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <Input
                      id="campaign-name"
                      type="text"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter campaign name"
                      disabled={loading || saving}
                      required
                      className="h-12 rounded-[12px]"
                    />
                  )}
                />
                {errors.name ? (
                  <p className="text-sm text-red-600">{errors.name.message?.toString()}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign-description">Description</Label>
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <Textarea
                      id="campaign-description"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Describe the campaign purpose, goals, audience, and rollout notes"
                      disabled={loading || saving}
                      className="min-h-[160px] rounded-[12px]"
                    />
                  )}
                />
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="campaign-status">Status</Label>
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(value) => field.onChange(value as CAMPAIGN_STATUSES)}
                        disabled={loading || saving}
                      >
                        <SelectTrigger
                          id="campaign-status"
                          className="h-12 rounded-[12px]"
                        >
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {CAMPAIGN_STATUS_OPTIONS.map((option) => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {CAMPAIGN_STATUS_LABELS[option]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="campaign-start-date">Start Date *</Label>
                  <Controller
                    control={control}
                    name="startDate"
                    render={({ field }) => (
                      <DatePickerV3
                        value={toPickerDate(field.value)}
                        onChange={(date) => field.onChange(toDateValue(date))}
                        disabled={loading || saving}
                        className="h-12 rounded-[12px]"
                        placeholder="Select start date"
                        ariaLabel="Campaign start date"
                      />
                    )}
                  />
                  {errors.startDate ? (
                    <p className="text-sm text-red-600">{errors.startDate.message?.toString()}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="campaign-end-date">End Date *</Label>
                  <Controller
                    control={control}
                    name="endDate"
                    render={({ field }) => (
                      <DatePickerV3
                        value={toPickerDate(field.value)}
                        onChange={(date) => field.onChange(toDateValue(date))}
                        disabled={loading || saving}
                        className="h-12 rounded-[12px]"
                        placeholder="Select end date"
                        ariaLabel="Campaign end date"
                      />
                    )}
                  />
                  {errors.endDate ? (
                    <p className="text-sm text-red-600">{errors.endDate.message?.toString()}</p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[18px] border border-dashed border-[#D5E3DB] bg-[#FBFDFC] p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-[#F2FCFA] p-2 text-[#00A86B]">
                    <FolderTree className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#004232]">Programs and Opportunities</h3>
                    <p className="mt-1 text-sm text-[#6A7282]">
                      After saving, you can manage programs and nested opportunities in the next
                      step.
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </Card>

          <div className="space-y-4 lg:sticky lg:top-[96px] lg:self-start">
            <Card className="rounded-[20px] border-[#DDEBE3] p-5 shadow-[0px_12px_30px_rgba(0,66,50,0.06)]">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-[#F2FCFA] p-3 text-[#00A86B]">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-medium uppercase tracking-[0.18em] text-[#6A7282]">
                    Progress
                  </div>
                  <div className="text-lg font-semibold text-[#004232]">Ready to save</div>
                </div>
              </div>
              <div className="mt-5 space-y-3 text-sm">
                <ChecklistItem
                  label="Campaign name"
                  complete={nameValue.trim().length > 0}
                />
                <ChecklistItem
                  label="Description"
                  complete={descriptionValue.trim().length > 0}
                />
                <ChecklistItem
                  label="Status"
                  complete={Boolean(statusValue)}
                />
                <ChecklistItem
                  label="Start date"
                  complete={Boolean(startDateValue)}
                />
                <ChecklistItem
                  label="End date"
                  complete={Boolean(endDateValue)}
                />
              </div>
            </Card>

            <Card className="rounded-[20px] border-[#DDEBE3] p-5 shadow-[0px_12px_30px_rgba(0,66,50,0.06)]">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-[#F2FCFA] p-3 text-[#00A86B]">
                  <FolderTree className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-medium uppercase tracking-[0.18em] text-[#6A7282]">
                    Next Step
                  </div>
                  <div className="text-lg font-semibold text-[#004232]">Program workspace</div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-6 text-[#5D6B63]">
                After saving the campaign, move into the program workspace to manage programs and
                nested opportunities.
              </p>
            </Card>
          </div>
        </div>
      </div>
    );
  },
);

function ChecklistItem({ label, complete }: { label: string; complete: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#EDF2EF] bg-white px-3 py-2.5">
      <CheckCircle2 className={`h-4 w-4 ${complete ? "text-[#00A86B]" : "text-[#C7D3CC]"}`} />
      <span className={complete ? "text-[#0F172A]" : "text-[#6A7282]"}>{label}</span>
    </div>
  );
}

export default CampaignEditorForm;
